import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PrimaryNavigation } from '../../components/navigation/PrimaryNavigation';
import Icon from '../../components/AppIcon';
import RegistrationForm from './components/RegistrationForm';
import TrustSignals from './components/TrustSignals';
import LoginRedirect from './components/LoginRedirect';

const UserRegistration = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const handleRegistration = async (formData) => {
    setIsLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 2000));

      localStorage.setItem('userProfile', JSON.stringify({
        fullName: formData?.fullName,
        email: formData?.email,
        dateOfBirth: formData?.dateOfBirth,
        retirementAge: formData?.retirementAge,
        registeredAt: new Date()?.toISOString()
      }));

      localStorage.setItem('isAuthenticated', 'true');
      
      setRegistrationSuccess(true);

      setTimeout(() => {
        navigate('/retirement-planning-setup');
      }, 2000);
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation />
      <main className="main-content main-content-desktop">
        <div className="max-w-7xl mx-auto">
          {registrationSuccess ? (
            <div className="max-w-md mx-auto">
              <div className="bg-card rounded-2xl p-8 border border-border shadow-card text-center space-y-6">
                <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto">
                  <Icon name="CheckCircle" size={32} className="text-success" />
                </div>
                
                <div>
                  <h2 className="text-2xl font-semibold text-foreground mb-2">
                    Registration Successful!
                  </h2>
                  <p className="text-muted-foreground">
                    Your account has been created successfully. Redirecting you to setup your retirement plan...
                  </p>
                </div>

                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <span>Setting up your financial planning dashboard</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div className="space-y-6">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                    <Icon name="Sparkles" size={16} />
                    <span>Start Your Financial Journey</span>
                  </div>
                  
                  <h1 className="text-4xl font-semibold text-foreground">
                    Create Your Account
                  </h1>
                  
                  <p className="text-lg text-muted-foreground">
                    Join thousands of Indians planning their financial future with confidence
                  </p>
                </div>

                <div className="bg-card rounded-2xl p-8 border border-border shadow-card">
                  <RegistrationForm onSubmit={handleRegistration} isLoading={isLoading} />
                </div>

                <LoginRedirect />
              </div>

              <div className="lg:sticky lg:top-24 space-y-6">
                <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 border border-primary/20">
                  <div className="space-y-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                      <Icon name="TrendingUp" size={24} className="text-primary" />
                    </div>
                    
                    <h2 className="text-2xl font-semibold text-foreground">
                      Why Choose Fire by Dexterr?
                    </h2>
                    
                    <div className="space-y-3">
                      {[
                        {
                          icon: "Target",
                          title: "Indian-Specific Planning",
                          description: "Calculations based on Indian inflation rates and financial norms"
                        },
                        {
                          icon: "PiggyBank",
                          title: "Retirement & Life Goals",
                          description: "Plan for retirement, home, education, and major life events"
                        },
                        {
                          icon: "LineChart",
                          title: "Real-Time Projections",
                          description: "See how your financial goals evolve with inflation adjustments"
                        },
                        {
                          icon: "Shield",
                          title: "Secure & Private",
                          description: "Your financial data is encrypted and never shared"
                        }
                      ]?.map((feature, index) => (
                        <div key={index} className="flex items-start gap-3 bg-card/50 rounded-lg p-4">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Icon name={feature?.icon} size={16} className="text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-foreground mb-1">{feature?.title}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {feature?.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <TrustSignals />
              </div>
            </div>
          )}
        </div>
      </main>
      <footer className="border-t border-border bg-card mt-16">
        <div className="main-content py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon name="TrendingUp" size={20} className="text-primary" />
                <span className="text-sm">
                  © {new Date()?.getFullYear()} Fire by Dexterr. All rights reserved.
                </span>
              </div>
              
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <button className="hover:text-foreground transition-colors focus-ring rounded">
                  About Us
                </button>
                <button className="hover:text-foreground transition-colors focus-ring rounded">
                  Contact
                </button>
                <button className="hover:text-foreground transition-colors focus-ring rounded">
                  Help Center
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserRegistration;